import { inject, Injectable, signal } from '@angular/core';
import { AppCookieService } from './app-cookie.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User, UserRole } from '../../shared/models/user.model';

export interface Me { email?: string; userCode?: string; }

@Injectable({ providedIn: 'root' })
export class AuthService {

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  public isLoggedIn$ = this.currentUser$.pipe(map(user => !!user));
  public currentUserRoles$: any;
  private http = inject(HttpClient);
  user = signal<Me | null>(null);
  private xsrfToken: string | null = null;
  private cookieService = inject(AppCookieService);
  public isLoggingOut = false;

  constructor() {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return;
    try {
      const user: User = JSON.parse(stored);
      this.currentUserSubject.next(user);
    } catch {
      this.clearLocalSession();
    }
  }

  /** Validates the session cookie against the backend on page load, then fetches the full profile. */
  async hydrate(): Promise<any> {
    try {
      const token = this.xsrfToken ?? localStorage.getItem('XSRF-TOKEN');
      const validToken = token && token !== 'undefined' ? token : null;
      let headers = new HttpHeaders().set('Client-Type', 'WebApp');
      if (validToken) headers = headers.set('X-XSRF-TOKEN', validToken);

      const me = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/Auth/Me`, { withCredentials: true, headers })
      );
      this.user.set(me || null);

      if (!me?.data?.userCode) {
        this.clearLocalSession();
        return null;
      }

      // Fetch the full profile (name, role, org details, etc.) using the validated userCode
      const profile = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/User/GetUserProfile`, {
          params: { userCode: me.data.userCode },
          withCredentials: true
        })
      );

      if (profile?.data && typeof profile.data.roleId !== 'undefined' && profile.data.roleId !== null) {
        const normalized = { ...profile.data, roleId: Number(profile.data.roleId) };
        localStorage.setItem('currentUser', JSON.stringify(normalized));
        this.currentUserSubject.next(normalized);
      } else {
        this.clearLocalSession();
      }
      return profile ?? null;
    } catch {
      this.clearLocalSession();
      return null;
    }
  }

  /** Seeds the CSRF cookie and stores the token locally. */
  async csrf(): Promise<void> {
    try {
      const resp = await firstValueFrom(this.http.get<{
        success?: boolean;
        data?: { xsrfToken?: string };
      }>(`${environment.apiUrl}/Auth/Csrf`, { withCredentials: true }));

      this.xsrfToken = resp?.data?.xsrfToken ?? this.cookieService.get('XSRF-TOKEN') ?? null;
      localStorage.setItem('XSRF-TOKEN', this.xsrfToken ?? '');
    } catch {
      this.xsrfToken = null;
    }
  }

  async login(email: string, password: string): Promise<void> {
    await this.csrf();
    const token = this.xsrfToken ?? this.cookieService.get('XSRF-TOKEN');
    const validToken = token && token !== 'undefined' ? token : null;
    const headers = validToken ? new HttpHeaders().set('X-XSRF-TOKEN', validToken) : undefined;

    const resp = await firstValueFrom(
      this.http.post<any>(
        `${environment.apiUrl}/Auth/Login`,
        { email, password },
        { withCredentials: true, headers }
      )
    );

    const userObj = resp?.data ?? null;
    this.user.set(userObj);

    if (userObj && typeof userObj.roleId !== 'undefined') {
      const normalized = { ...userObj, roleId: Number(userObj.roleId) };
      localStorage.setItem('currentUser', JSON.stringify(normalized));
      this.currentUserSubject.next(normalized);
    } else {
      this.clearLocalSession();
      throw new Error(resp?.message ?? 'Login failed.');
    }
  }

  /**
   * Self-service "forgot password" entry point. The backend always returns success — whether
   * or not the email has an account — so there is nothing to branch on here; the caller just
   * shows the generic "check your email" message this resolves with.
   */
  async forgotPassword(email: string): Promise<void> {
    await firstValueFrom(
      this.http.post<any>(
        `${environment.apiUrl}/Auth/ForgotPassword`,
        { email },
        { withCredentials: true }
      )
    );
  }

  /**
   * Checks a password-reset oobCode (from the link in the reset email) without consuming it,
   * and resolves the email it belongs to. No CSRF token needed — this is a read-only lookup.
   * Throws if the link is invalid/expired; the caller shows the returned error.
   */
  async verifyResetCode(oobCode: string): Promise<{ email: string }> {
    const resp = await firstValueFrom(
      this.http.post<any>(
        `${environment.apiUrl}/Auth/VerifyResetCode`,
        { oobCode },
        { withCredentials: true }
      )
    );
    return resp?.data ?? null;
  }

  /**
   * Confirms a password reset — consumes the oobCode. Mirrors login()'s CSRF handling since,
   * like Login, this is a state-changing action reachable while signed out.
   */
  async setPassword(oobCode: string, newPassword: string): Promise<{ email: string }> {
    await this.csrf();
    const token = this.xsrfToken ?? this.cookieService.get('XSRF-TOKEN');
    const validToken = token && token !== 'undefined' ? token : null;
    const headers = validToken ? new HttpHeaders().set('X-XSRF-TOKEN', validToken) : undefined;

    const resp = await firstValueFrom(
      this.http.post<any>(
        `${environment.apiUrl}/Auth/SetPassword`,
        { oobCode, newPassword },
        { withCredentials: true, headers }
      )
    );
    return resp?.data ?? null;
  }

  /**
   * Silently exchanges the refresh-token cookie for a new 24-hour session cookie.
   * Called by tokenRefreshInterceptor on 401 before giving up and redirecting.
   */
  refreshToken(): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/Auth/RefreshToken`,
      {},
      { withCredentials: true }
    );
  }

  async logout(): Promise<void> {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/Auth/Logout`, {}, { withCredentials: true })
      );
    } catch { /* best-effort */ }
    // Remove storage first WITHOUT emitting on currentUserSubject —
    // emitting null triggers isLoggedIn = false which tears down the shell
    // while the route is still /dashboard, causing a visible flash.
    // The full-page reload starts a fresh Angular instance so the subject
    // update is unnecessary.
    localStorage.removeItem('currentUser');
    localStorage.removeItem('XSRF-TOKEN');
    window.location.href = '/login';
  }

  /** Clears all local auth state without hitting the backend. */
  clearLocalSession(): void {
    this.user.set(null);
    this.currentUserSubject.next(null);
    this.xsrfToken = null;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('XSRF-TOKEN');
  }

  get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  hasRole(roles: (UserRole | string)[]): boolean {
    const user = this.currentUserValue;
    if (!user) return false;
    const userRole = typeof user.role === 'string' ? user.role : String(user.role);
    return roles.includes(userRole);
  }

  isViewOnly(): boolean {
    const user = this.currentUserValue;
    if (!user) return true;
    return user.role === UserRole.Admin || user.role === UserRole.DME_Admin;
  }

  isAdmin(): boolean {
    return this.hasRole([UserRole.DME_User, UserRole.DME_Admin, UserRole.Admin]);
  }
}
